import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const appSpinnerVariants = cva(
  'transition-all duration-500 ease-in-out',
  {
    variants: {
      variant: {
        default: 'text-primary',
        secondary: 'text-secondary',
        muted: 'text-muted-foreground/70',
      },
      size: {
        xs: '',
        sm: '',
        md: '',
        lg: '',
        xl: '',
      },
      visibility: {
        visible: 'opacity-100',
        hiding: 'opacity-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      visibility: 'visible',
    },
  }
);

export interface AppSpinnerProps 
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof appSpinnerVariants> {
  /**
   * Whether to hide the spinner with a fade-out animation
   * @default false
   */
  fadeOut?: boolean;
  
  /**
   * Optional message to display below the spinner
   */
  message?: string;
  
  /**
   * Callback function to be executed when fade-out animation completes
   */
  onFadeComplete?: () => void;
  
  /**
   * Whether to use a glowing background effect with blur
   * @default false
   */
  glowEffect?: boolean;
}

/**
 * Application spinner component with fade in/out animations and optional message
 */
export function AppSpinner({
  className,
  variant,
  size = 'md',
  fadeOut = false,
  visibility,
  message,
  onFadeComplete,
  glowEffect = false,
  ...props
}: AppSpinnerProps) {
  // Determine visibility state based on fadeOut prop
  const visibilityState = fadeOut ? 'hiding' : (visibility || 'visible');
  
  // Handle transition end event for fade-out animation
  const handleTransitionEnd = (e: React.TransitionEvent) => {
    // Only trigger callback if it exists and we're fading out
    if (fadeOut && onFadeComplete && e.propertyName === 'opacity') {
      onFadeComplete();
    }
  };

  // Map component size to pixel values for the spinner
  const spinnerSizeClass = {
    xs: 'w-4 h-4',
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
    xl: 'w-12 h-12',
  }[size || 'md'];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        glowEffect && 'p-8 rounded-xl bg-background/80 backdrop-blur-sm shadow-lg ring-1 ring-primary/10',
        appSpinnerVariants({ variant, size, visibility: visibilityState }),
        className
      )}
      onTransitionEnd={handleTransitionEnd}
      {...props}
    >
      <div className={cn(
        spinnerSizeClass,
        'animate-spin',
        {
          'text-primary': variant === 'default',
          'text-secondary': variant === 'secondary',
          'text-muted-foreground/70': variant === 'muted',
        }
      )}>
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      
      {message && (
        <p className={cn(
          'text-sm font-medium',
          {
            'text-foreground': variant === 'default',
            'text-secondary-foreground': variant === 'secondary',
            'text-muted-foreground': variant === 'muted',
          }
        )}>
          {message}
        </p>
      )}
    </div>
  );
}

AppSpinner.displayName = 'AppSpinner';
